import React, { useMemo, Suspense } from "react";
import { Row, Col, Tag } from "antd";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import { widgetRegistry } from "./widgetRegistry";
import WidgetCard from "./WidgetCard";
import "./Dashboard.css";

/* ---------- helpers ---------- */
const RESPONSIVE_BREAKPOINTS = ["md", "lg", "xl"];
const ROW_GROUPS = [
  ["super-user", "quick-links"],
  ["preview-envs"],
  ["build-status", "eterno-changelog"],
  ["panic-button"],
];
const widgetLoaders = {
  BuildStatusWidget: () => import("./widgets/BuildStatusWidget"),
  EternoChangelogWidget: () => import("./widgets/EternoChangelogWidget"),
  PanicButtonWidget: () => import("./widgets/PanicButtonWidget"),
  PreviewEnvsWidget: () => import("./widgets/PreviewEnvsWidget"),
  QuickLinksWidget: () => import("./widgets/QuickLinksWidget"),
  SuperUserWidget: () => import("./widgets/SuperUserWidget"),
};

/**
 * Map a widget component name to a lazy-loaded React component.
 * Each widget lives under ./widgets/<ComponentName>.js
 */
const lazyWidgetCache = {};
function getLazyWidget(componentName) {
  const loadWidget = widgetLoaders[componentName];
  if (!loadWidget) {
    throw new Error(`Unknown dashboard widget: ${componentName}`);
  }

  if (!lazyWidgetCache[componentName]) {
    lazyWidgetCache[componentName] = React.lazy(loadWidget);
  }
  return lazyWidgetCache[componentName];
}

/** Derive role string from the user object. */
function getUserRole(user) {
  if (!user) return "guest";
  if (user.isOfcAdmin) return "admin";
  if (user.isOfcStaff) return "cs";
  return "guest";
}

/** Format today's date in Italian (e.g. "lunedi 9 marzo 2026"). */
function formatDateItalian() {
  return new Date().toLocaleDateString("it-IT", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** Extract first name from full name or email. */
function getFirstName(user) {
  if (!user) return "";
  if (user.name) {
    return user.name.split(" ")[0];
  }
  if (user.email) {
    return user.email.split("@")[0];
  }
  return "";
}

function distributeRowSpans(widgets, breakpoint) {
  if (widgets.length === 0) return new Map();
  if (widgets.length === 1) {
    return new Map([[widgets[0].id, 24]]);
  }

  const baseSpans = widgets.map((widget) => widget.defaultSpan[breakpoint] || 24);
  const totalBaseSpan = baseSpans.reduce((sum, span) => sum + span, 0);

  if (totalBaseSpan === 24) {
    return new Map(widgets.map((widget) => [widget.id, widget.defaultSpan[breakpoint]]));
  }

  const scaledSpans = baseSpans.map((span) => (span / totalBaseSpan) * 24);
  const integerSpans = scaledSpans.map((span) => Math.floor(span));
  let remaining = 24 - integerSpans.reduce((sum, span) => sum + span, 0);

  scaledSpans
    .map((span, index) => ({
      index,
      remainder: span - integerSpans[index],
    }))
    .sort((left, right) => right.remainder - left.remainder)
    .forEach(({ index }) => {
      if (remaining <= 0) return;
      integerSpans[index] += 1;
      remaining -= 1;
    });

  return new Map(
    widgets.map((widget, index) => [widget.id, integerSpans[index]])
  );
}

function buildSpanOverrides(activeWidgets) {
  const activeWidgetsById = new Map(activeWidgets.map((widget) => [widget.id, widget]));
  const overrides = new Map();

  ROW_GROUPS.forEach((rowIds) => {
    const rowWidgets = rowIds
      .map((rowId) => activeWidgetsById.get(rowId))
      .filter(Boolean);

    RESPONSIVE_BREAKPOINTS.forEach((breakpoint) => {
      const distributedSpans = distributeRowSpans(rowWidgets, breakpoint);

      distributedSpans.forEach((span, widgetId) => {
        const widgetOverrides = overrides.get(widgetId) || {};
        widgetOverrides[breakpoint] = span;
        overrides.set(widgetId, widgetOverrides);
      });
    });
  });

  return overrides;
}

/* ---------- component ---------- */

function Dashboard() {
  const { user, isAuthenticated } = useAuth0();
  const { isProd } = useAppContext();

  const role = getUserRole(user);

  // Show all widgets whose defaultRoles includes the current role
  const activeWidgets = useMemo(
    () => widgetRegistry.filter((w) => w.defaultRoles.includes(role)),
    [role]
  );
  const spanOverrides = useMemo(
    () => buildSpanOverrides(activeWidgets),
    [activeWidgets]
  );

  if (!isAuthenticated) {
    return <div />;
  }

  /* ---------- render ---------- */
  return (
    <div className="dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <span className="dashboard-header__greeting">
          Ciao, {getFirstName(user)}
        </span>
        <div className="dashboard-header__meta">
          <span className="dashboard-header__date">{formatDateItalian()}</span>
          <Tag color={isProd ? "red" : "blue"}>
            {isProd ? "PROD" : "DEV"}
          </Tag>
        </div>
      </div>

      {/* Widget grid */}
      <Row gutter={[16, 16]}>
        {activeWidgets.map((widget) => {
          const LazyWidget = getLazyWidget(widget.component);
          const widgetSpan = spanOverrides.get(widget.id) || {};
          return (
            <Col
              key={widget.id}
              xs={widget.defaultSpan.xs}
              sm={widget.defaultSpan.sm}
              md={widgetSpan.md || widget.defaultSpan.md}
              lg={widgetSpan.lg || widget.defaultSpan.lg}
              xl={widgetSpan.xl || widget.defaultSpan.xl}
            >
              <Suspense
                fallback={
                  <WidgetCard title={widget.title} loading />
                }
              >
                <WidgetCard title={widget.title} extra={widget.headerExtra}>
                  <LazyWidget />
                </WidgetCard>
              </Suspense>
            </Col>
          );
        })}
      </Row>

    </div>
  );
}

export default Dashboard;
